import type { RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import { authGuard } from './guards/auth.guard';
import { metaGuard } from './guards/meta.guard';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/modules/home/views/Home.vue'),
    meta: {
      requiresAuth: true,
      title: 'Inicio',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/modules/auth/views/Login.vue'),
    meta: {
      requiresVisitor: true,
      title: 'Login',
      metaTags: [
        {
          name: 'description',
          content: 'Inicio de sesión',
        },
        {
          property: 'og:description',
          content: 'Inicio de sesión',
        },
      ],
    },
  },
  {
    path: '/registro',
    name: 'register',
    component: () => import('@/modules/auth/views/Register.vue'),
    meta: {
      requiresVisitor: true,
      title: 'Registrate',
      metaTags: [
        {
          name: 'description',
          content: 'Registro',
        },
        {
          property: 'og:description',
          content: 'Registro',
        },
      ],
    },
  },
  {
    path: '/reset-password/:passwordResetToken?',
    name: 'resetPassword',
    component: () => import('@/modules/auth/views/ResetPassword.vue'),
    props: true,
    meta: {
      requiresVisitor: true,
      title: 'Restablecer contraseña',
      metaTags: [
        {
          name: 'description',
          content: 'Restablecer contraseña',
        },
        {
          property: 'og:description',
          content: 'Restablecer contraseña',
        },
      ],
    },
  },
  {
    path: '/bienvenido/:newClientToken',
    name: 'welcome',
    props: true,
    component: () => import('@/modules/initialConfiguration/views/Welcome.vue'),
    meta: {
      title: 'Bienvenido a Munzen',
    },
  },
  {
    path: '/configuracion-inicial/:newClientToken',
    name: 'InitialConfiguration',
    props: true,
    component: () => import('@/modules/initialConfiguration/views/InitialConfiguration.vue'),
    meta: {
      title: 'Configuración inicial',
    },
  },
  {
    path: '/clientes',
    component: () => import('@/modules/clients/layouts/ClientLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'clients',
        component: () => import('@/modules/clients/views/ClientsList.vue'),
        meta: {
          title: 'Mis Clientes',
        },
      },
      {
        path: 'nuevo',
        name: 'clients.create',
        component: () => import('@/modules/clients/views/ClientCreate.vue'),
        meta: {
          title: 'Nuevo Cliente',
        },
      },
      {
        path: 'editar/:id',
        name: 'clients.edit',
        component: () => import('@/modules/clients/views/ClientEdit.vue'),
        // Cast route param to number so the component receives a numeric id
        props: route => ({ id: Number(route.params.id) }),
        meta: {
          title: 'Editar Cliente',
        },
      },
      {
        path: 'importar',
        name: 'clients.import',
        component: () => import('@/modules/clients/views/ClientImport.vue'),
        meta: {
          title: 'Importar Clientes',
        },
      },
      {
        path: 'importar/reporte',
        name: 'clients.import.report',
        component: () => import('@/modules/clients/views/ClientImportReport.vue'),
        meta: {
          title: 'Reporte de Importación',
        },
      },
      {
        path: 'importar/usos-cfdi',
        name: 'clients.import.usosCfdi',
        component: () => import('@/modules/clients/views/ClientImportSatCatalogs.vue'),
        meta: {
          title: 'Catálogo de Usos de CFDI',
        },
      },
      {
        path: 'importar/formas-pago',
        name: 'clients.import.formasPago',
        component: () => import('@/modules/clients/views/ClientImportSatCatalogs.vue'),
        meta: {
          title: 'Catálogo de Formas de Pago',
        },
      },
      {
        path: 'importar/regimenes-fiscales',
        name: 'clients.import.regimenesFiscales',
        component: () => import('@/modules/clients/views/ClientImportSatCatalogs.vue'),
        meta: {
          title: 'Catálogo de Regímenes Fiscales',
        },
      },
    ],
  },
  {
    path: '/configuraciones',
    component: () => import('@/modules/configs/layouts/ConfigLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'configs',
        component: () => import('@/modules/configs/views/ConfigsMenu.vue'),
        meta: {
          title: 'Configuraciones',
        },
      },
      {
        path: 'sellos-certificados-digitales',
        name: 'configs.sellosDigitales',
        component: () => import('@/modules/sellosDigitales/views/SellosDigitales.vue'),
        meta: {
          title: 'Sellos de los Certificados Digitales',
        },
      },
      {
        path: 'series-folios',
        name: 'configs.seriesFolios',
        component: () => import('@/modules/seriesFolios/views/SeriesFolios.vue'),
        meta: {
          title: 'Series y Folios',
        },
      },
      {
        path: 'mis-datos-fiscales',
        name: 'configs.enterprise',
        component: () => import('@/modules/enterprise/views/Enterprise.vue'),
        meta: {
          title: 'Mis Datos Fiscales',
        },
      },
      {
        path: 'grupos-de-impuestos',
        name: 'configs.taxesGroups',
        component: () => import('@/modules/taxesGroups/views/TaxesGroupsList.vue'),
        meta: {
          title: 'Grupos de Impuestos',
        },
      },
    ],
  },
  {
    path: '/productos-servicios',
    component: () => import('@/modules/productsServices/layouts/ProductServiceLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'productsServices',
        component: () => import('@/modules/productsServices/views/ProductsServicesList.vue'),
        meta: {
          title: 'Mis Productos / Servicios',
        },
      },
      {
        path: 'nuevo',
        name: 'productsServices.create',
        component: () => import('@/modules/productsServices/views/ProductServiceCreate.vue'),
        meta: {
          title: 'Nuevo Producto / Servicio',
        },
      },
      {
        path: 'editar/:id',
        name: 'productsServices.edit',
        component: () => import('@/modules/productsServices/views/ProductServiceEdit.vue'),
        // Cast route param to number so the component receives a numeric id
        props: route => ({ id: Number(route.params.id) }),
        meta: {
          title: 'Editar Producto / Servicio',
        },
      },
      {
        path: 'importar',
        name: 'productsServices.import',
        component: () => import('@/modules/productsServices/views/ProductServiceImport.vue'),
        meta: {
          title: 'Importar Productos / Servicios',
        },
      },
      {
        path: 'importar/reporte',
        name: 'productsServices.import.report',
        component: () => import('@/modules/productsServices/views/ProductServiceImportReport.vue'),
        meta: {
          title: 'Reporte de Importación',
        },
      },
      {
        path: 'importar/catalogo-productos-servicios',
        name: 'productsServices.import.catalogoProductosServicios',
        component: () => import('@/modules/productsServices/views/ProductServiceSatCatalog.vue'),
        meta: {
          title: 'Catálogo de Productos/Servicios SAT',
        },
      },
      {
        path: 'importar/catalogo-unidades',
        name: 'productsServices.import.catalogoUnidades',
        component: () => import('@/modules/productsServices/views/ProductServiceSatUnits.vue'),
        meta: {
          title: 'Catálogo de Unidades SAT',
        },
      },
    ],
  },
  {
    path: '/facturacion',
    component: () => import('@/modules/facturacion/layouts/FacturacionLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'facturacion',
        component: () => import('@/modules/facturacion/views/FacturacionMenu.vue'),
        meta: {
          title: 'Facturación',
        },
      },
      {
        path: 'pagos-pendientes',
        name: 'facturacion.pagosPendientes',
        component: () => import('@/modules/recepcionPagos/views/RecepcionPagoPendingPayments.vue'),
        meta: {
          title: 'Facturas con Pagos Pendientes',
        },
      },
      {
        path: 'facturas',
        component: () => import('@/modules/facturas/layouts/FacturasLayout.vue'),
        children: [
          {
            path: '',
            name: 'facturacion.facturas',
            component: () => import('@/modules/facturas/views/FacturasList.vue'),
            meta: {
              title: 'Mis Facturas',
            },
          },
          {
            path: 'nueva',
            name: 'facturacion.facturas.create',
            component: () => import('@/modules/facturas/views/FacturaCreate.vue'),
            meta: {
              title: 'Nueva Factura',
            },
          },
          {
            path: 'detalle/:facturaId',
            name: 'facturacion.facturas.detail',
            component: () => import('@/modules/facturas/views/FacturaDetail.vue'),
            props: route => ({ facturaId: Number(route.params.facturaId) }),
            meta: {
              title: 'Detalle de Factura',
            },
          },
          {
            path: 'detalle-pre-factura/:facturaId',
            name: 'facturacion.facturas.detailPreFactura',
            component: () => import('@/modules/facturas/views/FacturaDetail.vue'),
            props: route => ({ facturaId: Number(route.params.facturaId) }),
            meta: {
              title: 'Detalle de Pre Factura',
            },
          },
          {
            path: 'editar/:facturaId',
            name: 'facturacion.facturas.edit',
            component: () => import('@/modules/facturas/views/PreFacturaEdit.vue'),
            props: route => ({ facturaId: Number(route.params.facturaId) }),
            meta: {
              title: 'Editar Pre Factura',
            },
          },
          {
            path: 'clonar/:facturaId',
            name: 'facturacion.facturas.clone',
            component: () => import('@/modules/facturas/views/FacturaClone.vue'),
            props: route => ({ facturaId: Number(route.params.facturaId) }),
            meta: {
              title: 'Clonar Factura',
            },
          },
        ],
      },
      {
        path: 'facturas-globales',
        component: () => import('@/modules/facturasGlobales/layouts/FacturasGlobalesLayout.vue'),
        children: [
          {
            path: '',
            name: 'facturacion.facturas-globales',
            component: () => import('@/modules/facturasGlobales/views/FacturasGlobalesList.vue'),
            meta: {
              title: 'Mis Facturas Globales',
            },
          },
          {
            path: 'nueva/:month/:year',
            name: 'facturacion.facturas-globales.create',
            component: () => import('@/modules/facturasGlobales/views/FacturaGlobalCreate.vue'),
            meta: {
              title: 'Nueva Factura Global',
            },
          },
          {
            path: 'detalle/:facturaGlobalId',
            name: 'facturacion.facturas-globales.detail',
            component: () => import('@/modules/facturasGlobales/views/FacturaGlobalDetail.vue'),
            props: route => ({ facturaGlobalId: Number(route.params.facturaGlobalId) }),
            meta: {
              title: 'Detalle de Factura Global',
            },
          },
          {
            path: 'editar/:facturaGlobalId',
            name: 'facturacion.facturas-globales.edit',
            component: () => import('@/modules/facturasGlobales/views/FacturaGlobalEdit.vue'),
            props: route => ({ facturaGlobalId: Number(route.params.facturaGlobalId) }),
            meta: {
              title: 'Editar Factura Global',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/comprar-creditos',
    component: () => import('@/modules/buyCredits/layouts/BuyCreditsLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'buyCredits',
        component: () => import('@/modules/buyCredits/views/BuyCredits.vue'),
        meta: {
          title: 'Comprar Créditos',
        },
      },
      {
        path: 'check-out',
        name: 'buyCredits.checkOut',
        component: () => import('@/modules/buyCredits/views/CheckOut.vue'),
        meta: {
          title: 'Compra Realizada',
        },
      },
    ],
  },
  {
    path: '/mis-compras-de-creditos',
    component: () => import('@/modules/myCreditsOrders/layouts/MyCreditsOrdersLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'myCreditsOrders',
        component: () => import('@/modules/myCreditsOrders/views/MyCreditsOrdersList.vue'),
        meta: {
          title: 'Mis Compras de Créditos',
        },
      },
      {
        path: 'detalle/:id',
        name: 'myCreditsOrders.detail',
        component: () => import('@/modules/myCreditsOrders/views/MyCreditsOrderDetail.vue'),
        props: route => ({ id: Number(route.params.id) }),
        meta: {
          title: 'Detalle de Compra de Créditos',
        },
      },
    ],
  },
  {
    path: '/historial-de-gastos-de-creditos',
    component: () =>
      import('@/modules/spendingCreditsHistory/layouts/SpendingCreditsHistoryLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'spendingCreditsHistory',
        component: () =>
          import('@/modules/spendingCreditsHistory/views/SpendingCreditsHistoryList.vue'),
        meta: {
          title: 'Historial de Gastos de Créditos',
        },
      },
    ],
  },
  {
    path: '/centro-de-ayuda',
    component: () => import('@/modules/helpCenter/layouts/HelpCenterLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'helpCenter',
        component: () => import('@/modules/helpCenter/views/HelpCenter.vue'),
        meta: {
          title: 'Centro de Ayuda',
        },
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/app/views/Forbidden.vue'),
    meta: {
      title: 'Acceso denegado',
    },
  },
  {
    path: '/offline',
    name: 'offline',
    component: () => import('@/app/pwa/views/Offline.vue'),
    meta: {
      title: 'Sin conexión',
    },
  },
  // Catch-all route for 404 pages - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/app/views/NotFound.vue'),
    meta: {
      title: 'Página no encontrada',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    } else {
      return { top: 0, behavior: 'smooth' };
    }
  },
});

// Navigation Guards
router.beforeEach(authGuard);
router.beforeEach(metaGuard);

export default router;
